import {
  addDays,
  compareAsc,
  differenceInCalendarDays,
  isAfter,
} from 'date-fns'
import { get, isEqual } from 'lodash'
import {
  change,
  getFormMeta,
  getFormValues,
  isDirty,
  updateSyncWarnings,
} from 'redux-form'
import * as Effects from 'redux-saga/effects'
import { calculateEndPrice } from '../../../actions/bookingCalendar/reservationModal/calculateEndPrice'
import { IReservationFormChange } from '../../../actions/bookingCalendar/reservationModal/onReservationFormChange'
import {
  fireToastError,
  fireToastWarning,
} from '../../../actions/toasts/fireToast'
import edit from '../../../actions/toasts/icons/edit'
import rotate from '../../../actions/toasts/icons/rotate'
import { ICreateReservationFormValues } from '../../../containers/Form/CreateReservation/CreateReservation'
import { driver, names } from '../../../database/localStorage'
import i18n from '../../../i18n'
import { LicenseType } from '../../../models/License'
import { ReservationModalActions } from '../../../reducers/bookingCalendar/actionNames'
import getInvoice from '../../../reducers/invoice/getInvoice'
import { getLicense } from '../../../reducers/userAccount/selectors/license'
import store from '../../../store'
import { getApiUrlForEnv } from '../../../utils/ApiUrl'
import { isInScope } from '../../../hooks/useIsScope'
import { PermissionScope } from '../../../containers/Employees'
import { isSafari } from '../../../components/DetectBrowser/DetectBrowser'

const recalculatePerPerson = (
  values: Partial<ICreateReservationFormValues>,
) => {
  const { adultsCount, childrenCount, daysCount, pricePerPerson } = values

  const pricePerDay =
    Number(pricePerPerson) * (Number(adultsCount) + Number(childrenCount))

  return {
    endPrice: pricePerDay * (daysCount || 1),
    pricePerDay,
  }
}

function* onChange(action: IReservationFormChange) {
  const meta = yield Effects.select(getFormMeta(action.formName))

  const dirty = yield Effects.select(isDirty(action.formName))

  const inScope = yield Effects.select(isInScope)

  const invoice = yield Effects.select(getInvoice)

  if(invoice.link && inScope([PermissionScope.SCOPE_CAN_USE_INVOICES])){
    if(action.prevValues.advancePaymentType !== action.values.advancePaymentType || action.prevValues.payOnPlacePaymentType !== action.values.payOnPlacePaymentType){
      yield Effects.put(
        fireToastError(
          i18n.t('BED_20388_invoice_data_change'),
          5000,
          'Event',
          'InvoiceValuesChanged',
          true,
          i18n.t('edit'),
          () => {
            if (isSafari()) {
              this.windowReference.location = invoice.link
            } else {
              window.open(invoice.link, '_blank')
            }
          },
          edit(),
        ),
      )
    }
  }

  if (dirty) {
    if (
      action.prevValues.endPrice &&
      (!isEqual(action.values.dateFrom, action.prevValues.dateFrom) ||
        !isEqual(
          Number(action.values.daysCount),
          Number(action.prevValues.daysCount),
        ) ||
        !isEqual(action.values.dateTo, action.prevValues.dateTo) ||
        !isEqual(
          Number(action.values.adultsCount),
          Number(action.prevValues.adultsCount),
        ) ||
        !isEqual(
          Number(action.values.childrenCount),
          Number(action.prevValues.childrenCount),
        ) ||
        !isEqual(action.values.children, action.prevValues.children) ||
        !isEqual(action.values.feeding, action.prevValues.feeding))
    ) {
      if (
        (action.values.priceType === 1 ||
          action.values.priceType === 2 ||
          action.values.priceType === 3) &&
        action.formName === 'create_reservation'
      ) {
        if (action.values.priceType === 3) {
          const { endPrice, pricePerDay } = recalculatePerPerson(action.values)
          yield Effects.put(
            change(action.formName, 'endPrice', endPrice.toFixed(2)),
          )

          yield Effects.put(
            change(action.formName, 'pricePerDay', pricePerDay.toFixed(2)),
          )
        } else {
          yield Effects.put(
            change(
              action.formName,
              'pricePerPerson',
              (
                (Number(action.values.pricePerDay) || 1) /
                (Number(action.values.adultsCount) +
                  Number(action.values.childrenCount))
              ).toFixed(2),
            ),
          )

          yield Effects.put(
            change(
              action.formName,
              'endPrice',
              (
                Number(action.values.pricePerDay) *
                Number(action.values.daysCount)
              ).toFixed(2),
            ),
          )
        }
        yield Effects.put(updateSyncWarnings(action.formName, {}, {}))
      } else {
        yield Effects.put(
          fireToastWarning(
            i18n.t('BBO_977_recalculate_final_price'),
            null,
            'Event',
            'recalculatePrice',
            true,
            i18n.t('count_price'),
            () => {
              if (action.values.priceType === 3) {
                const localAction = {
                  form: action.formName,
                  type: 'recalculate price per person',
                }
                store.dispatch(updateSyncWarnings(action.formName, {}, {}))
                store.dispatch(localAction)
              } else if (action.values.priceType === 2) {
                const localAction = {
                  form: action.formName,
                  type: 'recalculate price per day',
                }
                store.dispatch(updateSyncWarnings(action.formName, {}, {}))
                store.dispatch(localAction)
              } else if (action.values.priceType === 1) {
                const localAction = {
                  form: action.formName,
                  type: 'recalculate end price',
                }
                store.dispatch(localAction)
                store.dispatch(updateSyncWarnings(action.formName, {}, {}))
              } else {
                store.dispatch(calculateEndPrice(action.formName))
              }
            },
            rotate(),
          ),
        )
        yield Effects.put(
          updateSyncWarnings(action.formName, { endPrice: ' ' }, {}),
        )
      }
    }
    if (
      action.values.timeToPayDate !== action.prevValues.timeToPayDate &&
      compareAsc(action.values.dateFrom, action.values.timeToPayDate) < 0 &&
      ((meta.timeToPayDate && meta.timeToPayDate.active) ||
        (meta.timeToPayDays && meta.timeToPayDays.active))
    ) {
      yield Effects.put(
        fireToastError(
          i18n.t('toast_due_date_error'),
          null,
          'Event',
          'dueDataError',
        ),
      )
    }

    if (
      action.values.dateTo &&
      compareAsc(action.values.dateTo, action.values.dateFrom) <= 0
    ) {
      yield Effects.put(
        fireToastError(
          i18n.t('reservation_error_end_date_before_start_date'),
          null,
          'Event',
          'reservationDateBeforeStartError',
        ),
      )
      yield Effects.put(change(action.formName, 'daysCount', 1))
      yield Effects.put(
        change(action.formName, 'dateTo', addDays(action.values.dateFrom, 1)),
      )
    }

    if (
      action.values.endPrice !== action.prevValues.endPrice ||
      action.values.advancePaymentPrice !==
        action.prevValues.advancePaymentPrice
    ) {
      yield Effects.put(
        change(
          action.formName,
          'payOnPlacePrice',
          (
            (isNaN(Number(action.values.endPrice))
              ? 0
              : Number(action.values.endPrice)) -
            (isNaN(Number(action.values.advancePaymentPrice))
              ? 0
              : Number(action.values.advancePaymentPrice))
          ).toFixed(2),
        ),
      )
    }

    if (action.values.endPrice !== action.prevValues.endPrice) {
      if (meta.endPrice && meta.endPrice.active) {
        const pricePerDay =
          Number(action.values.endPrice) /
          (isNaN(Number(action.values.daysCount))
            ? 1
            : Number(action.values.daysCount))

        yield Effects.put(
          change(action.formName, 'pricePerDay', pricePerDay.toFixed(2)),
        )

        yield Effects.put(
          change(
            action.formName,
            'pricePerPerson',
            (
              pricePerDay /
              (Number(action.values.adultsCount) +
                Number(action.values.childrenCount))
            ).toFixed(2),
          ),
        )

        yield Effects.put(change(action.formName, 'priceType', 2))
      }
    }

    if (action.values.pricePerDay !== action.prevValues.pricePerDay) {
      if (meta.pricePerDay && meta.pricePerDay.active) {
        yield Effects.put(change(action.formName, 'priceType', 1))

        yield Effects.put(
          change(
            action.formName,
            'pricePerPerson',
            (
              Number(action.values.pricePerDay) /
              (Number(action.values.adultsCount) +
                Number(action.values.childrenCount))
            ).toFixed(2),
          ),
        )

        yield Effects.put(
          change(
            action.formName,
            'endPrice',
            (
              Number(action.values.pricePerDay) *
              Number(action.values.daysCount)
            ).toFixed(2),
          ),
        )
      }

      yield Effects.put(updateSyncWarnings(action.formName, {}, {}))
    }

    if (action.values.pricePerPerson !== action.prevValues.pricePerPerson) {
      if (meta.pricePerPerson && meta.pricePerPerson.active) {
        yield Effects.put(change(action.formName, 'priceType', 3))

        const { endPrice, pricePerDay } = recalculatePerPerson(action.values)
        yield Effects.put(
          change(action.formName, 'endPrice', endPrice.toFixed(2)),
        )
        yield Effects.put(
          change(action.formName, 'pricePerDay', pricePerDay.toFixed(2)),
        )
      }

      yield Effects.put(updateSyncWarnings(action.formName, {}, {}))
    }

    if (
      action.values.timeToWaitDays &&
      action.values.timeToWaitDays !== action.prevValues.timeToWaitDays
    ) {
      yield Effects.put(
        change(
          action.formName,
          'timeToWaitDate',
          addDays(new Date(), action.values.timeToWaitDays),
        ),
      )
    }

    if (
      action.values.timeToPayDays &&
      action.values.timeToPayDays !== action.prevValues.timeToPayDays
    ) {
      yield Effects.put(
        change(
          action.formName,
          'timeToPayDate',
          addDays(new Date(), action.values.timeToPayDays),
        ),
      )
    }

    if (action.values.timeToWaitDate !== action.prevValues.timeToWaitDate) {
      if (action.values.timeToWaitDate) {
        yield Effects.put(
          change(
            action.formName,
            'timeToWaitDays',
            differenceInCalendarDays(action.values.timeToWaitDate, new Date()),
          ),
        )
      } else {
        yield Effects.put(change(action.formName, 'timeToWaitDays', ''))
      }
    }

    if (action.values.timeToPayDate !== action.prevValues.timeToPayDate) {
      if (action.values.timeToPayDate) {
        yield Effects.put(
          change(
            action.formName,
            'timeToPayDays',
            differenceInCalendarDays(action.values.timeToPayDate, new Date()),
          ),
        )
      } else {
        yield Effects.put(change(action.formName, 'timeToPayDays', ''))
      }
    }

    if (
      ifChanged(action.prevValues, action.values, [
        'timeToPayDate',
        'timeToWaitDate',
        'reservationStatus',
        'endPrice',
        'pricePerDay',
        'services',
        'company',
        'guest.name',
        'guest.street',
        'guest.houseNumber',
        'guest.apartmentNumber',
        'guest.city',
        'guest.postCode',
        'guest.country',
      ])
    ) {
      const invoice = yield Effects.select(getInvoice)
      const invoiceLicense = yield Effects.select(
        getLicense(LicenseType.LICENSE_INVOICE),
      )
      const key = driver.getItem(names.CredentialsStorage.AccessToken, '')

      if (invoice.link && inScope([PermissionScope.SCOPE_CAN_USE_INVOICES])) {
        yield Effects.put(
          fireToastError(
            i18n.t('BKO_852_invoice_data_change'),
            5000,
            'Event',
            'InvoiceValuesChanged',
            true,
            i18n.t('edit'),
            () => {
              window.open(
                invoiceLicense && isAfter(invoiceLicense.license, new Date())
                  ? invoice.link
                  : `${getApiUrlForEnv()}/index/login?autologinkey=${key}&redirect=/payment`,
                '_blank',
              )
            },
            edit(),
          ),
        )
      }
    }
  }
}

function ifChanged(
  prev: ICreateReservationFormValues,
  next: ICreateReservationFormValues,
  conditions: string[],
) {
  return conditions
    .map(cond => !isEqual(get(prev, cond), get(next, cond)))
    .some(e => e)
}

function* endPrice(action) {
  const getForm = getFormValues(action.form)
  const values: ICreateReservationFormValues = yield Effects.select(getForm)

  yield Effects.put(
    change(
      action.form,
      'endPrice',
      (Number(values.pricePerDay) * Number(values.daysCount)).toFixed(2),
    ),
  )

  yield Effects.put(
    change(
      action.form,
      'pricePerPerson',
      (
        Number(values.pricePerDay) /
        (Number(values.adultsCount) + Number(values.childrenCount))
      ).toFixed(2),
    ),
  )
}

function* endPricePerPerson(action) {
  const getForm = getFormValues(action.form)
  const values: ICreateReservationFormValues = yield Effects.select(getForm)

  const { endPrice, pricePerDay } = recalculatePerPerson(values)

  yield Effects.put(change(action.form, 'endPrice', endPrice.toFixed(2)))

  yield Effects.put(change(action.form, 'pricePerDay', pricePerDay.toFixed(2)))
}

/*function* pricePerDay(action) {
  const getForm = getFormValues(action.form)
  const values: ICreateReservationFormValues = yield Effects.select(getForm)

  yield Effects.put(change(action.form, 'pricePerDay',
    (Number(values.endPrice) / Number(values.daysCount)).toFixed(2)))
}*/
export default function*() {
  yield Effects.takeEvery(ReservationModalActions.OnChange, onChange)
  yield Effects.takeEvery('recalculate price per day', endPrice)
  yield Effects.takeEvery('recalculate price per person', endPricePerPerson)
  yield Effects.takeEvery('recalculate end price', endPrice)
}
